<template>
  <div class="home">
    <!-- Search Bar -->
    <div class="search-section">
      <div class="search-bar-wrapper">
        <el-input
          v-model="searchQuery"
          :placeholder="$t('nav.searchPlaceholder')"
          class="search-input"
          @keyup.enter.native="handleSearch"
        >
          <template slot="suffix">
            <span class="search-icon-btn" @click="handleSearch">
              <img src="@/assets/images/search.png" alt="search" class="search-icon" />
            </span>
          </template>
        </el-input>
      </div>
    </div>

    <!-- Hero Banner Carousel + Stats -->
    <section class="hero-carousel-section">
      <el-carousel
        :interval="5000"
        arrow="never"
        :autoplay="true"
        :height="carouselHeight"
        indicator-position="none"
        ref="heroCarousel"
        @change="onCarouselChange"
      >
        <el-carousel-item v-for="item in bannerList" :key="item.posterId">
          <div class="hero" @click="onBannerClick(item)">
            <img
              :src="getImage(item.posterImageAddr)"
              :alt="item.posterName"
              class="hero-img"
              @error="onImgError"
            />
          </div>
        </el-carousel-item>
      </el-carousel>

      <!-- Fixed content layer on top of carousel -->
      <div class="hero-fixed-layer">
        <div class="hero-fixed-inner">
          <!-- Right: stats -->
          <div class="hero-stats">
            <div class="stat-item">
              <img src="@/assets/images/total.png" alt="publications" class="stat-icon" />
              <div class="stat-number">{{ formatNumber(stats.bookCount) }}</div>
              <div class="stat-label">{{ $t("home.statPublications") }}</div>
            </div>
            <div class="stat-item">
              <img src="@/assets/images/people.png" alt="authors" class="stat-icon" />
              <div class="stat-number">{{ formatNumber(stats.authorCount) }}</div>
              <div class="stat-label">{{ $t("home.statAuthors") }}</div>
            </div>
            <div class="stat-item">
              <img src="@/assets/images/download.png" alt="downloads" class="stat-icon" />
              <div class="stat-number">{{ formatNumber(stats.downloadCount) }}</div>
              <div class="stat-label">{{ $t("home.statDownloads") }}</div>
            </div>
          </div>
        </div>

        <!-- Carousel Dots (overlaid at bottom of banner) -->
        <div class="dots">
          <span
            v-for="(item, index) in bannerList"
            :key="index"
            class="dot"
            :class="{ active: activeCarousel === index }"
            @click="goToSlide(index)"
          ></span>
        </div>
      </div>
    </section>

    <!-- Explore Publications -->
    <section class="explore">
      <div class="section-inner">
        <h2>{{ $t("home.exploreTitle") }}</h2>
        <div class="category-tags" v-loading="categoryLoading">
          <span
            v-for="(cat, index) in categoryList"
            :key="cat.categoryId || index"
            class="tag"
            :class="{ active: activeCategory === index }"
            @click="onCategoryChange(index)"
          >{{ translateCategory(cat.categoryName) }}</span>
        </div>
        <el-empty
          v-if="!categoryLoading && !categoryList.length"
          :description="$t('common.empty')"
        ></el-empty>
      </div>
    </section>

    <!-- Book Recommendation -->
    <section class="book-recommend" v-loading="configLoading">
      <div class="section-inner">
        <h2>{{ $t("home.bookRecommend") }}</h2>
        <div v-if="bookConfig.bookId" class="book-contain">
            <div class="book-cover" >
              <img
                v-if="bookConfig.bookImageAddr"
                :src="getImage(bookConfig.bookImageAddr)"
                :alt="bookConfig.bookName"
                class="book-cover-img"
                @error="onImgError"
              />
              <div v-else class="book-cover-placeholder"></div>
            </div>
            <div class="book-info">
              <h3 class="book-title-zh">{{ bookConfig.bookName || $t("common.empty") }}</h3>
              <p v-if="bookConfig.bookNameEn" class="book-title-en">{{ bookConfig.bookNameEn }}</p>
              <p v-if="bookConfig.authorNames && bookConfig.authorNames.length > 0" class="book-author">{{ bookConfig.authorNames.join('、') + $t('home.authorSuffix') }}</p>
              <p class="book-desc">{{ bookConfig.bookIntroduction }}</p>
              <div class="more-wrapper">
                <el-button type="text" class="more-btn" @click="goMore('book')">
                  {{ $t("home.more") }} &gt;
                </el-button>
              </div>
            </div>
        </div>
        <el-empty
          v-else-if="!configLoading"
          :description="$t('common.empty')"
        ></el-empty>
      </div>
    </section>

    <!-- Author Videos -->
    <section class="author-video" v-loading="configLoading">
      <div class="section-inner">
        <h2>{{ $t("home.videoTitle") }}</h2>
        <div
          v-if="bookConfig.authorFilmTitle || bookConfig.authorFilmImageAddr"
          class="author-contain"
        >
            <div v-if="bookConfig.authorFilmImageAddr" class="video-thumbnail" @click="playVideo">
              <img
                :src="getImage(bookConfig.authorFilmImageAddr)"
                :alt="bookConfig.authorFilmTitle || 'video'"
                class="video-bg"
                @error="onImgError"
              />
              <div v-if="bookConfig.authorFilmOuterAddr" class="video-play-btn">
                <i ></i>
              </div>
            </div>
            <div v-else class="video-placeholder"><i ></i></div>
            <div class="video-info">
              <h3>{{ $t("home.authorIntro") }}</h3>
              <div class="video-brief">
                <p :class="{ 'brief-en': isEn }">{{ authorBriefText }}</p>
              </div>
            </div>
            <div class="more-wrapper">
              <el-button v-if="bookConfig.authorFilmOuterAddr" type="text" class="more-btn" @click="goMore('video')">
                {{ $t("home.more") }} &gt;
              </el-button>
            </div>
        </div>
        <el-empty
          v-else-if="!configLoading"
          :description="$t('common.empty')"
        ></el-empty>
      </div>
    </section>
  </div>
</template>

<script>
import imgBg from "@/assets/images/img-bg.png";
import defaultVideoImg from "@/assets/images/footer-video.png";
import {getImage} from "@/utils/tools";
import noImg from "@/assets/images/no-img.png";
import {
  getPosterList,
  getCategoryList,
  getBookCategoryConfig,
  getStatistics,
} from "@/api/home";

export default {
  name: "HomePage",
  data() {
    return {
      searchQuery: "",
      activeCarousel: 0,
      activeCategory: 0,
      carouselHeight: "auto",
      bannerList: [],
      categoryList: [],
      bookConfig: {},
      authorFilmLoaded: false,
      stats: {
        bookCount: 0,
        authorCount: 0,
        downloadCount: 0,
      },
      categoryLoading: false,
      configLoading: false,
      defaultVideoImg,
      fallbackBanner: imgBg,
    };
  },
  computed: {
    isEn() {
      return this.$i18n.locale === "en";
    },
    authorBriefText() {
      if (this.isEn) {
        return this.$t("home.authorBioEn");
      }
      return this.bookConfig.authorFilmBrief || "";
    },
  },
  mounted() {
    this.calcCarouselHeight();
    window.addEventListener("resize", this.calcCarouselHeight);
    this.loadBanners();
    this.loadCategories();
    this.loadStatistics();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calcCarouselHeight);
  },
  methods: {
    getImage,
    onImgError(e) {
      e.target.src = noImg;
    },
    formatNumber(val) {
      const n = Number(val);
      return Number.isFinite(n) ? n.toLocaleString("en-US") + "+" : "0+";
    },
    translateCategory(name) {
      if (!name) return '';
      const key = `categoryNames.${name}`;
      const translated = this.$i18n.t(key);
      return translated === key ? name : translated;
    },
    async loadStatistics() {
      try {
        const res = await getStatistics();
        const data = res?.data || {};
        this.stats = {
          bookCount: data.bookCount || 0,
          authorCount: data.authorCount || 0,
          downloadCount: data.downloadCount || 0,
        };
      } catch (err) {
        // 保留默认 0 值
      }
    },
    async loadBanners() {
      try {
        const res = await getPosterList();
        const list = Array.isArray(res?.data) ? res.data : [];
        this.bannerList = list.length
          ? list
          : [{ bg: this.fallbackBanner }];
      } catch (err) {
        this.bannerList = [{ bg: this.fallbackBanner }];
      }
    },
    async loadCategories() {
      this.categoryLoading = true;
      try {
        const res = await getCategoryList({ categoryLevel: 1 });
        const list = Array.isArray(res?.data) ? res.data : res?.rows || [];
        this.categoryList = list;
        if (list.length) {
          this.loadBookConfig(list[0].categoryId);
        }
      } catch (err) {
        this.categoryList = [];
      } finally {
        this.categoryLoading = false;
      }
    },
    async loadBookConfig(categoryId) {
      this.configLoading = true;
      try {
        const res = await getBookCategoryConfig(
          categoryId ? { categoryId } : {}
        );
        const data = res?.data || {};
        // 处理作者列表，拼接作者名字（后缀在模板中动态拼接）
        if (data.authorList && Array.isArray(data.authorList)) {
          const authorNames = data.authorList
            .map(author => author.authorName)
            .filter(Boolean);
          this.bookConfig.authorNames = authorNames;
        }
        // 作者影片数据只加载一次（第一页签的数据）
        if (!this.authorFilmLoaded) {
          if (data.authorFilmBrief) {
            data.authorFilmBrief = data.authorFilmBrief
              .replace(/^(作者簡介|作者简介|Author Bio)\s*[:：]?\s*/, '')
              .trim();
          }
          this.bookConfig.authorFilmTitle = data.authorFilmTitle;
          this.bookConfig.authorFilmImageAddr = data.authorFilmImageAddr;
          this.bookConfig.authorFilmOuterAddr = data.authorFilmOuterAddr;
          this.bookConfig.authorFilmBrief = data.authorFilmBrief;
          this.authorFilmLoaded = true;
        }
        // 其他数据正常更新
        this.bookConfig.bookId = data.bookId;
        this.bookConfig.bookImageAddr = data.bookImageAddr;
        this.bookConfig.bookName = data.bookName;
        this.bookConfig.bookNameEn = data.bookNameEn;
        this.bookConfig.bookIntroduction = data.bookIntroduction;
      } catch (err) {
        this.bookConfig = {};
      } finally {
        this.configLoading = false;
      }
    },
    onCategoryChange(index) {
      this.activeCategory = index;
      const cat = this.categoryList[index];
      if (cat) this.loadBookConfig(cat.categoryId);
    },
    onBannerClick(item) {
      const link = item.linkUrl || item.outerAddr;
      if (link) window.open(link, "_blank");
    },
    calcCarouselHeight() {
      // 图片原始尺寸 1920x600，按比例计算高度
      const width = this.$refs.heroCarousel
        ? this.$refs.heroCarousel.$el.offsetWidth
        : window.innerWidth;
      this.carouselHeight = "600px";
      // this.carouselHeight = Math.round((width * 600) / 1920) + "px";
    },
    handleSearch() {
      if (this.searchQuery.trim()) {
        this.$router.push({ path: "/search", query: { q: this.searchQuery } });
      }
    },
    onCarouselChange(index) {
      this.activeCarousel = index;
    },
    goToSlide(index) {
      this.$refs.heroCarousel.setActiveItem(index);
    },
    playVideo() {
      const url = this.bookConfig.authorFilmOuterAddr;
      if (url) {
        window.open(url, "_blank");
      } else {
        this.$message.info(this.$t('common.empty'));
      }
    },
    goMore(type) {
      if (type === "book") {
        if (this.bookConfig.bookId) {
          this.$router.push({ path: `/book/${this.bookConfig.bookId}` });
        } else {
          this.$message.info(this.$t('common.empty'));
        }
      } else {
        const url = this.bookConfig.authorFilmOuterAddr;
        if (url) {
          window.open(url, "_blank");
        } else {
          this.$message.info(this.$t('common.empty'));
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ===== Search Section ===== */
.search-section {
  background: linear-gradient(270deg, #fe7819 0%, #ffa826 100%);
  padding: 15px 24px;
}

.search-bar-wrapper {
  max-width: 800px;
  margin: 0 auto;

  .search-input {
    width: 100%;

    ::v-deep .el-input__inner {
      border-radius: 25px;
      border: none;
      height: 50px;
      line-height: 50px;
      font-size: 14px;
      padding-left: 20px;
      padding-right: 50px;
      box-shadow: none;
    }

    ::v-deep .el-input__suffix {
      right: 5px;
      display: flex;
      align-items: center;
    }
  }

  .search-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }

  .search-icon {
    width: 20px;
    height: 20px;
  }
}

/* ===== Hero Carousel ===== */
.hero-carousel-section {
  position: relative;
}

.hero {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.hero-img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

/* Fixed layer that stays on top of carousel */
.hero-fixed-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  display: flex;
  flex-direction: column;
}

.hero-fixed-inner {
  flex: 1;
  max-width: $max-width;
  width: 100%;
  margin: 0 auto;
  padding: 60px 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

/* Right: stats */
.hero-stats {
  display: flex;
  align-items: center;
  gap: 40px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: $white;

  .stat-icon {
    width: 45px;
    height: 45px;
    margin-bottom: 12px;
  }

  .stat-number {
    font-size: 48px;
    font-weight: 700;
    line-height: 1;
  }

  .stat-label {
    font-size: 24px;
    opacity: 0.85;
    margin-top: 8px;
    letter-spacing: 1px;
  }
}

/* ===== Carousel Dots (overlaid inside banner) ===== */
.dots {
  pointer-events: auto;
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 0 0 30px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;

    &.active {
      background: $white;
      transform: scale(1.2);
    }

    &:hover {
      background: $white;
    }
  }
}

/* ===== More Button at section bottom ===== */
.more-wrapper {
  display: flex;
  justify-content: flex-start;
  margin-top: 20px;

  .more-btn {
    font-size: 16px;
    color: $title-color;
    padding: 0;

    &:hover {
      opacity: 0.8;
    }
  }
}

/* ===== Explore ===== */
.section-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 24px;
}

.explore {
  padding: 40px 0;

  h2 {
    font-size: 24px;
    letter-spacing: 2px;
    margin-bottom: 20px;
    color: $text-dark;
  }
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 18px 26px;

  .tag {
    padding: 8px 18px;
    border: 1px solid $border-color;
    border-radius: 20px;
    font-size: 16px;
    color: $text-gray;
    cursor: pointer;
    transition: all 0.2s;

    &:hover,
    &.active {
      border-color: $primary-orange;
      background: $bg-orange-light;
    }
  }
}

/* ===== Book Recommendation ===== */
.book-recommend {
  padding: 40px 0;
  background: #FFF0DD;
  h2 {
    font-size: 26px;
    letter-spacing: 2px;
    margin-bottom: 40px;
    color: $text-dark;
  }
  .book-contain {
    display: flex;
  }
}

.book-cover {
  flex-shrink: 0;
  width: 200px;
  height: 280px;
}

.book-cover-img {
  width: 200px;
  height: 280px;
  object-fit: cover;
  display: block;
}

.book-cover-placeholder {
  width: 200px;
  height: 280px;
  object-fit: contain;
  background: url("../../assets/images/no-img.png") no-repeat;
  background-size: 100% 100%;
}

.empty-tip {
  color: $text-gray;
  font-size: 13px;
}

.book-info {
  flex: 1 0;
  margin-left: 40px;
  position: relative;
  min-height: 280px;
  .book-title-zh {
    font-size: 24px;
    color: $title-color;
    margin-bottom: 10px;
  }

  .book-title-en {
    font-size: 22px;
    color: $title-color;
    margin-bottom: 20px;
    font-style: italic;
  }

  .book-author {
    font-size: 16px;
    color: $text-gray;
    margin-bottom: 10px;
  }

  .book-desc {
    font-size: 16px;
    line-height: 28px;
    color: $text-gray;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .more-wrapper {
    position: absolute;
    bottom: 0;
    right: 0;
    margin-top: 0;
  }
}

/* ===== Author Video ===== */
.author-video {
  padding: 40px 0;
  h2 {
    font-size:26px;
    letter-spacing: 2px;
    margin-bottom: 40px;
    color: $text-dark;
  }
  .author-contain {
  display: flex;
  position: relative;
  min-height: 300px; /* 确保有足够空间放置按钮 */
  
  .more-wrapper {
    position: absolute;
    bottom: 0;
    right: 0; /* 视频缩略图宽度 + 间距 */
    margin-top: 0;
  }
}
}

.video-thumbnail {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  width: 534px;
  height: 300px;
  border-radius: 15px;
  cursor: pointer;
  
  .video-bg {
    width: 100%;
    height: 300px;
    object-fit: cover;
    display: block;
  }

  .video-play-btn {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    i {
      content: url("../../assets/images/video-icon.png");
      width: 60px;
      height: 60px;
    }
  }

  &:hover .video-play-btn {
    background: rgba(232, 119, 34, 0.8);
  }
}

.video-info {
  flex: 1;
  margin-left: 60px;
  h1 {
    font-size: 26px;
    letter-spacing: 1px;
    color: $title-color;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 20px;
    font-weight: 700;
    color: $text-dark;
    margin-bottom: 20px;
    letter-spacing: 1px;
  }

  .video-brief {
    .brief-label {
      display: block;
      font-size: 20px;
      font-weight: 700;
      color: $text-dark;
      margin-bottom: 20px;
      letter-spacing: 1px;
    }

    p {
      font-size: 16px;
      line-height: 1.8;
      color: $text-gray;
      display: -webkit-box;
      -webkit-line-clamp: 7;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .brief-en {
      letter-spacing: 0;
    }
  }
}
.video-placeholder {
  border-radius: 8px;
  overflow: hidden;
  width: 534px;
  height: 300px;
  border-radius: 15px;
  background: rgba(173,173,173,0.4);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    content: url("../../assets/images/video-icon.png");
    width: 60px;
    height: 60px;
  }
}

.lang-en .home {
  .stat-label {
    letter-spacing: 0px;
  }

  .explore h2 {
    letter-spacing: 0px;
  }

  .category-tags .tag {
    letter-spacing: 0px;
  }

  .book-recommend h2 {
    letter-spacing: 0px;
  }

  .author-video h2 {
    letter-spacing: 0px;
  }

  .video-info h1 {
    letter-spacing: 0px;
  }

  .video-info h3 {
    letter-spacing: 0px;
  }

  .video-brief .brief-label {
    letter-spacing: 0px;
  }
}
</style>